import { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../../providers/LanguageProvider';

import { cn } from '@/lib/utils';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandEmpty,
} from '../ui/command';
import { Input } from '@/components/ui/input';
import Icons from '../../icons/icons';
import BasicLoader from '../common/basic-loader';

type SelectOption = {
  label: string;
  value: string;
};

interface EnhancedCreateSelectProps {
  label?: string;
  value?: string;  
  options: Array<string | SelectOption>;
  isLoading?: boolean;
  disabled?: boolean;
  onValueChange?: (value: string) => void;
  onCreateNewValue?: (value: string) => void;
  showCreate?: boolean;
}

const EnhancedCreateSelect = ({
  label,
  value,
  options,
  isLoading,
  disabled,
  onValueChange,
  onCreateNewValue,
  showCreate = true,
}: EnhancedCreateSelectProps) => {
  const [showValueList, setShowValueList] = useState(false);
  const [valueQuery, setValueQuery] = useState('');
  const [selectedValue, setSelectedValue] = useState(value ?? '');
  const valueCommandRef = useRef<HTMLDivElement>(null);

  const { messages } = useLanguage();
  const langData = messages?.Evaluation;

  useEffect(() => {
    setSelectedValue(value ?? '');
  }, [value]);

  /**
   * Close the value list when clicking outside of the component
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showValueList &&
        valueCommandRef.current &&
        !valueCommandRef.current.contains(event.target as Node)
      ) {
        setShowValueList(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [showValueList]);

  // Close the dropdown if the field becomes disabled while open
  useEffect(() => {
    if (disabled) {
      setShowValueList(false);
    }
  }, [disabled]);

  const normalizedOptions = options.map((option) =>
    typeof option === 'string' ? { label: option, value: option } : option
  );

  const trimmedQuery = valueQuery.trim();
  const hasExactMatch = normalizedOptions.some(
    (o) =>
      o.label.toLowerCase() === trimmedQuery.toLowerCase() ||
      o.value.toLowerCase() === trimmedQuery.toLowerCase()
  );
  const canCreate = showCreate && trimmedQuery !== '' && !hasExactMatch;

  return (
    <div className="flex flex-col gap-1">
      {label && <label className="text-sm text-gray-300">{label}</label>}
      <div className="relative" ref={valueCommandRef}>
        <Input
          placeholder="Select"
          className={cn(
            'h-[42px] rounded-sm border-form-control-border-rest capitalize text-white',
            disabled
              ? 'bg-form-input-disabled-bg cursor-not-allowed opacity-50'
              : 'cursor-pointer'
          )}
          value={selectedValue}
          readOnly
          disabled={disabled}
          onClick={() => {
            if (disabled) return;
            setValueQuery('');
            setShowValueList(true);
          }}
        />
        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
          {Icons.ChevronDownIcon('size-5 text-form-input-disabled')}
        </div>

        {showValueList && !disabled && (
          <div className="absolute z-10 mt-1 w-full rounded-sm border border-[#C1C9CF] bg-white shadow-sm">
            <Command className="rounded-sm rounded-b-none bg-white text-[#464642]">
              <CommandInput
                placeholder="Search or enter a value"
                className="text-[#323130] placeholder:text-[#8C9EA1]"
                iconColor="-ml-1"
                value={valueQuery}
                onValueChange={setValueQuery}
              />
              <CommandList className="max-h-40 overflow-y-auto">
                {isLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <BasicLoader className="inline size-4 align-middle">
                      <span className="ml-1.5 align-middle text-data-table-cell text-form-input-disabled">
                        {langData?.loading_templates}
                      </span>
                    </BasicLoader>
                  </div>
                ) : (
                  <>
                    <CommandGroup className="text-black">
                      {normalizedOptions.map((item) => (
                        <CommandItem
                          key={item.value}
                          value={item.value}
                          className="cursor-pointer capitalize data-[selected=true]:bg-[#EDEBE9] data-[selected=true]:text-[#323130]"
                          onSelect={() => {
                            setSelectedValue(item.label);
                            onValueChange?.(item.value);
                            setShowValueList(false);
                          }}
                        >
                          {item.label}
                        </CommandItem>
                      ))}
                    </CommandGroup>

                    <CommandEmpty className="border-b border-[#EBEFF1] text-data-table-cell text-form-input-disabled">
                      <div className="py-[7px] pl-3">{langData?.no_matching_value_found}</div>
                    </CommandEmpty>

                    {canCreate && (
                      <div
                        role="button"
                        tabIndex={0}
                        className="flex cursor-pointer items-center gap-2 border-b border-[#EBEFF1] py-[7px] pl-3 text-sm text-black hover:bg-[#EDEBE9]"
                        onClick={() => {
                          setSelectedValue(trimmedQuery);
                          onCreateNewValue?.(trimmedQuery);
                          setShowValueList(false);
                        }}
                      >
                        <span className="flex items-center gap-2 text-sm leading-none">
                          {Icons.PlusIcon('size-4')} {langData?.create}:
                        </span>
                        <span className="capitalize">{trimmedQuery}</span>
                      </div>
                    )}
                  </>
                )}
              </CommandList>
            </Command>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnhancedCreateSelect;
